import { Link } from "@tanstack/react-router";
import { FolderKanban } from "lucide-react";
import { useMemo, useState } from "react";
import { PageHeaderBar } from "@/components/layout/PageHeaderBar";
import { usePageHeader } from "@/components/layout/PageHeaderContext";
import { EmptyBook, isEmptyBook, YourBook } from "@/components/profitability/BookNotice";
import { BudgetCell } from "@/components/profitability/BudgetCell";
import { ClientPartnerCell } from "@/components/profitability/ClientPartnerCell";
import { ContractTypeSelect } from "@/components/profitability/ContractTypeSelect";
import { clientPartnerFilter, UNASSIGNED } from "@/components/profitability/clientPartner";
import {
  ActiveFilterBar,
  FilterButton,
  FilterDialog,
} from "@/components/profitability/FilterDialog";
import { ForecastBadge } from "@/components/profitability/ForecastBadge";
import type { FilterDef } from "@/components/profitability/filters";
import { MarginCell } from "@/components/profitability/MarginCell";
import { ResaleBadge } from "@/components/profitability/ResaleBadge";
import { DataTable, ROW_CLASS } from "@/components/shared/data-table";
import { EmptyState } from "@/components/shared/empty-state";
import { ErrorState } from "@/components/shared/error-state";
import { KpiTile } from "@/components/shared/kpi-tile";
import { CursorPagination } from "@/components/shared/pagination";
import { SearchInput } from "@/components/shared/search-input";
import { Card } from "@/components/ui/card";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useBookCount } from "@/hooks/use-book-count";
import { useContractTypeParam } from "@/hooks/use-contract-type-param";
import { DEFAULT_LIMIT, useDataGrid } from "@/hooks/use-data-grid";
import { useFilterParams } from "@/hooks/use-filter-params";
import { useHidesPersonLevel } from "@/hooks/use-my-access";
import { useProjects } from "@/hooks/use-projects";
import { useRangeParam } from "@/hooks/use-range-param";
import { useTabParam } from "@/hooks/use-tab-param";
import { COLUMN_HELP } from "@/lib/column-help";
import { money, percent } from "@/lib/format";
import type { ProjectListRow, ProjectsTotals } from "@/lib/profitability-api";

/** Module-level for the stable identity the filter hook and memos depend on. */
const PROJECT_FILTERS: FilterDef<ProjectListRow>[] = [clientPartnerFilter<ProjectListRow>()];
const PROJECT_FILTER_KEYS = PROJECT_FILTERS.map((def) => def.key);

/**
 * Applied by the SERVER, like the accounts grid's.
 *
 * A row here is a project-MONTH and the grid pages at 50 of 1,746, so a filter
 * applied in the browser would have offered only the partners visible on page
 * one and narrowed that page alone. The note says "every project-month" rather
 * than "every project" because that is what a row is, and the count beside it
 * is counted the same way.
 */
const FILTER_SCOPE =
  "Filters every project-month, not just this page -- the server applies it before paging.";

const EMPTY_ROWS: ProjectListRow[] = [];

type ProjectsView = "project" | "month";

/**
 * By project is the first level: "is this project making money over the
 * range" is the question the list is opened to answer. The hours budget is a
 * lifetime figure, so the month rows each repeated it; on a project row it is
 * said once, beside the money it belongs with.
 */
const DEFAULT_VIEW: ProjectsView = "project";

function asView(value: string): ProjectsView {
  return value === "month" ? "month" : "project";
}

/**
 * The summary strip, from the engine's whole-result-set totals. Rendered only
 * when the engine sends them: an older server has no `totals`, and four tiles
 * of zeros would read as a real answer.
 */
function ProjectsStrip({ totals }: { totals: ProjectsTotals }) {
  const marginHint =
    totals.margin_pct === null ? undefined : `${percent(totals.margin_pct)} margin`;
  return (
    <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
      <KpiTile
        label="Revenue"
        value={money(totals.revenue)}
        hint={`${totals.distinct_projects.toLocaleString()} projects`}
      />
      {/* Aggregate callers get no cost or profit, so Margin stands in. */}
      {totals.profit === undefined ? (
        <KpiTile label="Margin" value={percent(totals.margin_pct)} />
      ) : (
        <KpiTile label="Profit" value={money(totals.profit)} hint={marginHint} />
      )}
      <KpiTile
        label="Over budget"
        value={totals.over_budget_projects.toLocaleString()}
        tone={totals.over_budget_projects > 0 ? "bad" : "default"}
        hint="projects past their budgeted hours"
      />
      <KpiTile
        label="No budget set"
        value={totals.projects_without_budget.toLocaleString()}
        hint="hours burn cannot be assessed"
      />
    </div>
  );
}

export function ProjectsPage() {
  // Worst margin first: this list exists to find problems, not to admire the
  // healthy ones. The sortKey values below must match the Python endpoint's
  // sortable-column allowlist exactly, or the server returns 400.
  const grid = useDataGrid({
    // Biggest projects first. Worst-margin-first opened the grid on -2,747%
    // rows earning $68; the user can still sort by margin from the header.
    defaultSort: "revenue",
    defaultSortDir: "desc",
  });
  // Same ?range= the accounts grid uses -- without it this grid listed every
  // period on record beside an accounts grid showing twelve months.
  const [range, setRange] = useRangeParam();
  const [contractType, setContractType] = useContractTypeParam();
  // In the URL for the reason the range and contract type are: this screen gets
  // pasted into Slack, and a filter in component state reproduces as no filter
  // at all for whoever opens the link.
  const [filterValues, setFilterValues] = useFilterParams(PROJECT_FILTER_KEYS);
  // ?tab= for the reason every other view choice is in the URL: a pasted link
  // must open on the view the sender was looking at.
  const [tab, setTab] = useTabParam(DEFAULT_VIEW);
  const view = asView(tab);
  const { data, isLoading, isError, error, refetch } = useProjects(
    grid.query,
    grid.pageRequest,
    range,
    contractType,
    filterValues.client_partner,
    view,
  );
  // An engine older than view=project ignores the param and sends months.
  // Label the rows by what came back, not by what was asked for.
  const byProject = data?.view === "project";

  const [filtersOpen, setFiltersOpen] = useState(false);
  // The server already applied the filter, so these rows need no second pass.
  const rows = data?.rows ?? EMPTY_ROWS;
  // Options from the API's own partner list, never from the visible page. Note
  // this list is NOT the accounts grid's: it holds partners with project-level
  // hours who carry no account at all, so neither list is a superset.
  const filterOptions = useMemo(
    () => ({
      // No counts -- the server filters, so the page cannot know them, and a
      // printed 0 would be wrong rather than merely small.
      client_partner: [
        ...(data?.client_partners ?? []).map((value) => ({ value })),
        { value: UNASSIGNED },
      ],
    }),
    [data?.client_partners],
  );

  const contractTypes = data?.contract_types;
  const rangeInfo = data?.range;
  const distinctProjects = data?.distinct_projects ?? 0;
  const total = data?.total ?? 0;
  const rowNoun = byProject ? "project" : "project-month";
  // Published before the error branch, so a failed load still shows the title
  // and the filters that caused it rather than an unlabelled error.
  usePageHeader(
    () => (
      <PageHeaderBar
        title="All Projects"
        fields={[
          {
            // Kept, in the header's labelled slot rather than as a subtitle: a
            // row here is a project-MONTH, so `total` is not a project count,
            // and "1,746 projects" over a few hundred was simply wrong.
            key: "rows",
            label: "Showing",
            // Stacked, not one line: on one line this field plus the search,
            // Filters and two selects overflowed 1440 and wrapped the whole
            // control cluster onto a second row, clipping the search box.
            content: (
              <>
                <span className="text-sm font-medium tabular-nums">
                  {distinctProjects.toLocaleString()} projects
                </span>
                {byProject ? null : (
                  <span className="text-[11px] tabular-nums text-muted-foreground">
                    {total.toLocaleString()} project-months
                  </span>
                )}
              </>
            ),
          },
        ]}
        filters={
          <>
            <SearchInput
              value={grid.searchInput}
              onValueChange={grid.setSearch}
              placeholder="Project or account..."
              className="w-[220px]"
            />
            <FilterButton values={filterValues} onOpen={() => setFiltersOpen(true)} />
            <ContractTypeSelect
              types={contractTypes}
              value={contractType}
              onChange={setContractType}
            />
          </>
        }
        range={rangeInfo}
        rangeValue={range}
        onRangeChange={setRange}
      />
    ),
    [
      distinctProjects,
      total,
      byProject,
      grid.searchInput,
      grid.setSearch,
      filterValues,
      contractTypes,
      contractType,
      setContractType,
      rangeInfo,
      range,
      setRange,
    ],
  );

  const hidesPersonLevel = useHidesPersonLevel(data?.access);

  // The "Your book" count: Aggregate only, before search and filters.
  const bookCount = useBookCount("projects", range, data?.access !== undefined);

  if (isError) {
    return (
      <div className="w-full space-y-4">
        <ErrorState message={(error as Error)?.message} onRetry={() => refetch()} />
      </div>
    );
  }

  if (isEmptyBook(data?.access)) {
    return (
      <div className="w-full space-y-4">
        <EmptyBook access={data.access} />
      </div>
    );
  }

  return (
    <div className="w-full space-y-4">
      <YourBook access={data?.access} count={bookCount.data} noun={["project", "projects"]} />
      <FilterDialog
        open={filtersOpen}
        onOpenChange={setFiltersOpen}
        defs={PROJECT_FILTERS}
        options={filterOptions}
        values={filterValues}
        onApply={setFilterValues}
        scopeNote={FILTER_SCOPE}
      />

      <ActiveFilterBar
        defs={PROJECT_FILTERS}
        values={filterValues}
        shown={rows.length}
        total={data?.total ?? rows.length}
        // The server filtered, so there is no surviving-of-loaded ratio to
        // quote. Counted in project-MONTHS, matching what a row is and what
        // the header above says -- "472 projects match" would be wrong by a
        // factor of four, since 472 project-months is 120 projects.
        countLabel={`${(data?.total ?? rows.length).toLocaleString()} ${rowNoun}${
          (data?.total ?? rows.length) === 1 ? "" : "s"
        } match`}
        scopeNote={FILTER_SCOPE}
        onEdit={() => setFiltersOpen(true)}
        onClear={() => setFilterValues({})}
      />

      {data?.totals ? <ProjectsStrip totals={data.totals} /> : null}

      {/* Back to page one: a cursor into 1,746 project-months points past the
        end of a few hundred projects. */}
      <Tabs
        value={view}
        onValueChange={(next) => {
          grid.resetToFirstPage();
          setTab(next);
        }}
      >
        <TabsList>
          <TabsTrigger value="project">By project</TabsTrigger>
          <TabsTrigger value="month">By month</TabsTrigger>
        </TabsList>
      </Tabs>

      <Card flush className="w-full">
        <DataTable
          flush
          dense
          loading={isLoading}
          empty={
            <EmptyState
              icon={FolderKanban}
              title="No projects match"
              description="Try a different search term."
            />
          }
          sort={grid.sort}
          sortDir={grid.sortDir}
          onSort={grid.toggleSort}
          headers={[
            { label: "Project", sortKey: "project_name", help: COLUMN_HELP.project },
            { label: "Account", sortKey: "account_name", help: COLUMN_HELP.account },
            // client_partner_name, the flat field -- see the accounts list.
            {
              label: "Client Partner",
              sortKey: "client_partner_name",
              className: "whitespace-nowrap",
              help: `${COLUMN_HELP.clientPartner} ${COLUMN_HELP.clientPartnerInherited}`,
            },
            // Sorted on period_start, the real date -- sorting on the display
            // name is alphabetical ("April 2024" before "December 2023").
            // On a project row the column is the span the totals cover, sorted
            // on its first month.
            {
              label: byProject ? "Months" : "Period",
              sortKey: "period_start",
              className: "whitespace-nowrap",
              help: byProject ? COLUMN_HELP.months : COLUMN_HELP.period,
            },
            {
              label: "Revenue",
              sortKey: "revenue",
              className: "text-right",
              help: COLUMN_HELP.revenue,
            },
            // Project cost is one team's pay on a small project: the engine omits
            // it for Aggregate, so the column goes rather than a column of dashes.
            ...(hidesPersonLevel
              ? []
              : [
                  {
                    label: "Cost",
                    sortKey: "cost",
                    className: "text-right",
                    help: COLUMN_HELP.cost,
                  },
                ]),
            {
              label: "Margin",
              sortKey: "margin_pct",
              className: "text-right",
              help: COLUMN_HELP.margin,
            },
            {
              label: "Hours vs budget",
              sortKey: "variance_pct",
              className: "text-right whitespace-nowrap",
              help: COLUMN_HELP.hoursVsBudget,
            },
          ]}
        >
          {rows.map((row) => (
            <tr key={`${row.entity_id}-${row.period_id ?? "all"}`} className={ROW_CLASS}>
              <td className="px-4 py-2.5 font-medium">
                <Link
                  to="/dashboard/projects/$demandId"
                  params={{ demandId: row.entity_id }}
                  className="hover:underline"
                >
                  {row.project_name ?? row.entity_id}
                </Link>
                {row.is_passthrough ? <ResaleBadge className="ml-2" /> : null}
              </td>
              {/* The title doubles as the full name when the cell truncates and
                as the only explanation of the dash when there is no name. */}
              <td
                className="max-w-48 truncate px-4 py-2.5"
                title={row.account_name ?? "This demand is not attached to an account"}
              >
                {row.account_name ?? "—"}
              </td>
              <td className="max-w-48 px-4 py-2.5">
                <ClientPartnerCell owner={row.client_partner} />
              </td>
              <td className="px-4 py-2.5 text-muted-foreground">
                {/* nowrap: "October 2025" was breaking across two lines and
                  doubling the height of every row. */}
                <span className="flex items-center gap-2 whitespace-nowrap">
                  {byProject && row.period_count !== undefined ? (
                    <span title={row.period ?? undefined}>
                      {row.period_count} {row.period_count === 1 ? "month" : "months"}
                    </span>
                  ) : (
                    (row.period ?? row.period_id)
                  )}
                  <ForecastBadge isClosed={row.period_is_closed} />
                </span>
              </td>
              <td className="px-4 py-2.5 text-right tabular-nums">{money(row.revenue)}</td>
              {hidesPersonLevel ? null : (
                <td className="px-4 py-2.5 text-right tabular-nums">
                  {row.cost === undefined ? "—" : money(row.cost)}
                </td>
              )}
              <td className="px-4 py-2.5 text-right">
                <MarginCell value={row.margin_pct} />
              </td>
              <td className="px-4 py-2.5 text-right">
                <BudgetCell variancePct={row.variance_pct} hasBudget={row.has_budget} />
              </td>
            </tr>
          ))}
        </DataTable>
      </Card>

      <CursorPagination
        total={data?.total ?? 0}
        page={grid.page}
        limit={DEFAULT_LIMIT}
        hasNext={Boolean(data?.next_cursor)}
        hasPrev={Boolean(data?.prev_cursor)}
        onNext={() => grid.goNext(data?.next_cursor ?? null)}
        onPrev={() => grid.goPrev(data?.prev_cursor ?? null)}
        loading={isLoading}
        itemLabel={byProject ? "projects" : "project-months"}
      />
    </div>
  );
}
